/**
 * Currency Utilities for Marketing
 * Minimal implementation to pass failing tests
 */

interface PricingOptions {
  period?: 'month' | 'year';
  monthlyEquivalent?: number;
}

export function formatPrice(
  amount: number,
  currency: string,
  locale: string,
  options?: PricingOptions
): string {
  if (amount < 0) {
    throw new Error('Amount must be positive');
  }

  const minimumFractionDigits = options && 'minimumFractionDigits' in options
    ? (options as { minimumFractionDigits?: number }).minimumFractionDigits
    : 0;
  const maximumFractionDigits = options && 'maximumFractionDigits' in options
    ? (options as { maximumFractionDigits?: number }).maximumFractionDigits
    : 0;

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits,
    maximumFractionDigits
  });

  const formattedAmount = formatter.format(amount);

  if (options?.period) {
    const isGerman = locale?.toLowerCase().startsWith('de');
    const monthLabel = isGerman ? '/Monat' : '/month';
    const yearLabel = isGerman ? '/Jahr' : '/year';

    const periodText = options.period === 'month' ? monthLabel : yearLabel;

    if (options.period === 'year' && options.monthlyEquivalent) {
      const monthlyFormatted = formatter.format(options.monthlyEquivalent);
      return `${formattedAmount}${periodText} (${monthlyFormatted}${monthLabel})`;
    }

    return `${formattedAmount}${periodText}`;
  }

  return formattedAmount;
}

export function convertCurrency(
  amount: number, 
  fromCurrency: string, 
  toCurrency: string
): number {
  if (amount < 0) {
    throw new Error('Amount must be positive');
  }

  const supportedCurrencies = ['GBP', 'EUR', 'CHF'];
  
  if (!supportedCurrencies.includes(fromCurrency)) {
    throw new Error(`Unsupported source currency: ${fromCurrency}`);
  }
  
  if (!supportedCurrencies.includes(toCurrency)) {
    throw new Error(`Unsupported target currency: ${toCurrency}`);
  }

  if (fromCurrency === toCurrency) {
    return amount;
  }

  // Simple conversion rates (in real app, these would come from an API)
  const rates: Record<string, Record<string, number>> = {
    'CHF': { 'EUR': 0.9, 'GBP': 0.85 },
    'EUR': { 'CHF': 1.11, 'GBP': 0.94 },
    'GBP': { 'CHF': 1.18, 'EUR': 1.06 }
  };

  return Math.round(amount * rates[fromCurrency][toCurrency]);
}
